import { z } from 'zod';
import { paginationMetaSchema } from '../common/pagination';
import { moneySchema } from '../money';
import { projectStatusSchema } from '../projects/project.schema';
import { salesPipelineEntryResponseSchema } from '../sales/pipeline.schema';

/**
 * Rolling Forecast (ADR 0056): erwarteter Umsatz je Monat aus zwei Quellen —
 * aktive Projekte (voller Auftragswert im Monat von `plannedEndDate`) und die
 * Vertriebspipeline (Volumen × Auftragswahrscheinlichkeit im Monat des
 * erwarteten Auftragseingangs). Reine Read-Aggregation im Modul reporting.
 */
export const rollingForecastProjectSchema = z.object({
  projectId: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  legalEntityName: z.string().nullable(),
  status: projectStatusSchema,
  orderValue: moneySchema.nullable(),
  /**
   * Plan-Ende des Projekts — im Rolling Forecast als "Fertigstellung"
   * editierbar (Amendment 2026-07-21: kein eigenes Forecast-Feld mehr, um
   * doppelte Datenpflege zu vermeiden; siehe ADR 0056).
   */
  plannedEndDate: z.string().datetime().nullable(),
});
export type RollingForecastProject = z.infer<typeof rollingForecastProjectSchema>;

/** Eine Monatszeile des Umsatz-Forecasts (YYYY-MM), je Quelle + Summe. */
export const rollingForecastMonthSchema = z.object({
  month: z.string(), // 'YYYY-MM'
  label: z.string(), // z. B. 'Aug 2026'
  activeProjects: moneySchema, // voller Auftragswert je Plan-Ende-Monat
  pipelineWeighted: moneySchema, // Volumen × Wahrscheinlichkeit je Auftragseingangs-Monat
  total: moneySchema,
});
export type RollingForecastMonth = z.infer<typeof rollingForecastMonthSchema>;

/**
 * Der eigentliche Report: Umsatz je Monat + Pipeline. Die Monatswerte werden
 * serverseitig über ALLE aktiven Projekte + die gesamte Pipeline gerechnet
 * (günstige Aggregation über wenige Spalten). Die (potenziell große) Liste der
 * aktiven Projekte wird bewusst NICHT hier mitgeliefert, sondern separat
 * paginiert geladen (`rollingForecastActiveProjectsPageSchema`).
 */
export const rollingForecastResponseSchema = z.object({
  currency: z.enum(['EUR', 'USD', 'CHF']),
  /** Vertriebspipeline (voraussichtliche Projekte) — zur Anzeige im Register. */
  pipeline: z.array(salesPipelineEntryResponseSchema),
  /** Umsatz-Forecast je Monat, chronologisch. */
  months: z.array(rollingForecastMonthSchema),
  /** Summen über den gesamten Horizont. */
  totals: z.object({
    activeProjects: moneySchema,
    pipelineWeighted: moneySchema,
    total: moneySchema,
  }),
});
export type RollingForecastResponse = z.infer<typeof rollingForecastResponseSchema>;

/** Listen-Query für die paginierte Tabelle der aktiven Projekte (Fertigstellung). */
export const rollingForecastActiveQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(500).default(25),
});
export type RollingForecastActiveQuery = z.infer<typeof rollingForecastActiveQuerySchema>;

/** Eine Seite aktiver Projekte mit editierbarer Fertigstellung (paginiert). */
export const rollingForecastActiveProjectsPageSchema = z.object({
  currency: z.enum(['EUR', 'USD', 'CHF']),
  data: z.array(rollingForecastProjectSchema),
  meta: paginationMetaSchema,
});
export type RollingForecastActiveProjectsPage = z.infer<typeof rollingForecastActiveProjectsPageSchema>;
